import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./ItemHistory.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";

export default function ItemHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [history, setHistory] = useState([]);
  const [itemInfo, setItemInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchItemHistory = async () => {
    try {
      const historyRes = await axios.get(`${API_BASE}/inventory-history/item/${id}`);
      const rows = Array.isArray(historyRes.data) ? historyRes.data : [];
      setHistory(rows);

      if (rows.length > 0) {
        const firstRecord = rows[0];
        setItemInfo({
          id: firstRecord.inventory_id,
          name: firstRecord.product_name,
          category: firstRecord.category,
          stock: firstRecord.qty_after,
          price: 0,
        });
      }
    } catch (err) {
      console.error("Error fetching item history:", err);

      setItemInfo({
        id,
        name: `Item ${id}`,
        category: "Sample Category",
        stock: 50,
        price: 100,
      });

      setHistory([
        {
          id: 1,
          inventory_id: Number(id),
          product_name: `Item ${id}`,
          category: "Sample Category",
          change_type: "PURCHASE",
          qty_before: 0,
          qty_change: 100,
          qty_after: 100,
          reference_id: 1,
          remarks: "Initial stock added",
          created_at: new Date().toISOString(),
        },
        {
          id: 2,
          inventory_id: Number(id),
          product_name: `Item ${id}`,
          category: "Sample Category",
          change_type: "SALE",
          qty_before: 100,
          qty_change: -50,
          qty_after: 50,
          reference_id: 2,
          remarks: "Sold to customer",
          created_at: new Date(Date.now() - 86400000).toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchItemHistory();
    }
  }, [id]);

  const getChangeTypeClass = (type) => {
    switch (type) {
      case "PURCHASE":
        return "change-purchase";
      case "SALE":
        return "change-sale";
      case "ADJUSTMENT":
        return "change-adjustment";
      case "RETURN":
        return "change-return";
      default:
        return "change-default";
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    return new Date(value).toLocaleString("en-IN");
  };

  const totalIn = history.reduce(
    (sum, record) => sum + (Number(record.qty_change) > 0 ? Number(record.qty_change) : 0),
    0
  );
  const totalOut = history.reduce(
    (sum, record) =>
      sum + (Number(record.qty_change) < 0 ? Math.abs(Number(record.qty_change)) : 0),
    0
  );
  const latestUpdate = history.length > 0 ? history[0]?.created_at : null;
  const backTarget = location.state?.from || "/inventory/history";

  if (loading) {
    return (
      <div className="inventory-history-container">
        <div className="history-loading-card">
          <div className="loading">Loading item history...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-history-container">
      <div className="item-history-hero">
        <div className="item-history-hero-main">
          <button className="item-history-back-btn" onClick={() => navigate(backTarget)}>
            <span className="item-history-back-btn-icon" aria-hidden="true">{"\u2190"}</span>
            <span>Inventory History</span>
          </button>
          <div className="item-history-title-group">
            <p className="item-history-eyebrow">Inventory Tracking</p>
            <h1>Inventory History</h1>
            <p className="item-history-subtitle">
              Track stock movement, adjustments, and the latest balance for this item.
            </p>
          </div>
        </div>
        <div className="item-history-hero-badge">
          Item ID: <strong>#{id}</strong>
        </div>
      </div>

      {itemInfo && (
        <div className="item-info-card">
          <div className="item-info-header">
            <div>
              <p className="item-info-label">Product</p>
              <h2>{itemInfo.name}</h2>
              <p className="item-info-category">{itemInfo.category}</p>
            </div>
            <div className="item-stock-chip">Current Stock: {itemInfo.stock}</div>
          </div>

          <div className="item-history-summary">
            <div className="item-history-summary-card item-history-summary-blue">
              <span className="item-history-summary-label">Current Balance</span>
              <strong className="item-history-summary-value">{itemInfo.stock}</strong>
            </div>
            <div className="item-history-summary-card item-history-summary-green">
              <span className="item-history-summary-label">Total In</span>
              <strong className="item-history-summary-value">+{totalIn}</strong>
            </div>
            <div className="item-history-summary-card item-history-summary-red">
              <span className="item-history-summary-label">Total Out</span>
              <strong className="item-history-summary-value">-{totalOut}</strong>
            </div>
            <div className="item-history-summary-card">
              <span className="item-history-summary-label">Price</span>
              <strong className="item-history-summary-value">Rs.{itemInfo.price}</strong>
            </div>
          </div>

          <div className="item-details">
            <span>
              <strong>Category:</strong> {itemInfo.category}
            </span>
            <span>
              <strong>Current Stock:</strong> {itemInfo.stock}
            </span>
            <span>
              <strong>Price:</strong> Rs.{itemInfo.price}
            </span>
            <span>
              <strong>Last Updated:</strong> {formatDateTime(latestUpdate)}
            </span>
          </div>
        </div>
      )}

      <div className="history-table-container">
        <div className="history-table-header">
          <div>
            <h3>Transaction History</h3>
            <p>Every stock movement for this product in one place.</p>
          </div>
          <div className="history-table-count">{history.length} Entries</div>
        </div>

        {history.length === 0 ? (
          <div className="no-history">
            <h4>No history found</h4>
            <p>This item does not have any stock movement records yet.</p>
          </div>
        ) : (
          <div className="history-table-scroll">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Change Type</th>
                  <th>Quantity Before</th>
                  <th>Quantity Change</th>
                  <th>Quantity After</th>
                  <th>Reference</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr key={record.id}>
                    <td className="history-date-cell">{formatDateTime(record.created_at)}</td>
                    <td>
                      <span className={`change-type ${getChangeTypeClass(record.change_type)}`}>
                        {record.change_type}
                      </span>
                    </td>
                    <td>{record.qty_before ?? 0}</td>
                    <td
                      className={
                        Number(record.qty_change) > 0 ? "positive-change" : "negative-change"
                      }
                    >
                      {Number(record.qty_change) > 0 ? "+" : ""}
                      {record.qty_change}
                    </td>
                    <td>
                      <strong>{record.qty_after ?? 0}</strong>
                    </td>
                    <td>#{record.reference_id ?? "-"}</td>
                    <td>{record.remarks || "No remarks"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
